// Patient onboarding form configs extracted from ModalWizard.js as builder
// functions. Each builder takes its dependencies as args and returns the same
// form-config array that ModalWizard previously declared inline. Behavior is
// IDENTICAL to the inline definitions — only the location changes.
//
// Provider form configs (passwordFormConfig, supervisorClinicalRoleFormConfig,
// credentialsFormConfig, personalInfoFormConfig, lanuageFormConfig,
// referralFormConfig) intentionally remain inline in ModalWizard.js — see
// "extend, don't fork" decision in the patient-peptide-home spec.

// Step (patient): Identity + Contact Prefs + Emergency Contact Form Config
export function buildPatientIdentityContactFormConfig({ t, profile, setProfileValue, setProfileCustomValue, setNestedCustom }) {
  return [
    { type: "content", content: <h5>{t("wizard.patientIdentity.title")}</h5> },
    { type: "content", content: <p>{t("wizard.patientIdentity.subtitle")}</p> },
    {
      label: t("wizard.patientIdentity.dob"),
      type: "date",
      value: profile.dob || "",
      onChange: setProfileValue("dob"),
      required: true,
    },
    {
      label: t("wizard.patientIdentity.preferredContact"),
      type: "select",
      value: profile.custom?.preferredContactChannel || "",
      onChange: setProfileCustomValue("preferredContactChannel"),
      required: true,
      options: [
        { key: "", label: t("wizard.patientIdentity.selectChannel") },
        { key: "email", label: t("wizard.patientIdentity.channelEmail") },
        { key: "sms", label: t("wizard.patientIdentity.channelSms") },
      ],
    },
    {
      label: t("wizard.patientIdentity.emergencyContactName"),
      type: "text",
      value: profile.custom?.emergencyContact?.name || "",
      onChange: setNestedCustom("emergencyContact", "name"),
    },
    {
      label: t("wizard.patientIdentity.emergencyContactPhone"),
      type: "phone",
      value: profile.custom?.emergencyContact?.phone || "",
      onChange: setNestedCustom("emergencyContact", "phone"),
    },
  ];
}

// Step (patient): Shipping (+ optional billing) Form Config
// `sameAsBilling` defaults to checked (true) on first patient render via
// `profile.custom?.sameAsBilling !== false` (treat undefined as checked, since
// the useState initializer no longer pre-populates it). The field persists
// across re-renders. Billing fields show ONLY when explicitly === false.
export function buildPatientShippingFormConfig({ t, profile, setNestedCustom, toggleSameAsBilling, states }) {
  const setShippingField = (field) => setNestedCustom("shippingAddress", field);
  const setBillingField = (field) => setNestedCustom("billingAddress", field);

  return [
    { type: "content", content: <h5>{t("wizard.patientShipping.title")}</h5> },
    { type: "content", content: <p>{t("wizard.patientShipping.subtitle")}</p> },
    {
      label: t("wizard.patientShipping.street"),
      type: "text",
      value: profile.custom?.shippingAddress?.street || "",
      onChange: setShippingField("street"),
      required: true,
    },
    {
      label: t("wizard.patientShipping.aptUnit"),
      type: "text",
      value: profile.custom?.shippingAddress?.apt || "",
      onChange: setShippingField("apt"),
    },
    {
      label: t("wizard.patientShipping.city"),
      type: "text",
      value: profile.custom?.shippingAddress?.city || "",
      onChange: setShippingField("city"),
      required: true,
    },
    {
      label: t("wizard.patientShipping.state"),
      type: "select",
      value: profile.custom?.shippingAddress?.state || "",
      onChange: setShippingField("state"),
      required: true,
      options: [
        { key: "", label: t("wizard.patientShipping.selectState") },
        ...states.map((s) => ({ key: s.code, label: s.name })),
      ],
    },
    {
      label: t("wizard.patientShipping.zip"),
      type: "text",
      value: profile.custom?.shippingAddress?.zip || "",
      onChange: setShippingField("zip"),
      required: true,
    },
    {
      type: "content",
      content: (
        <div className="row-form vertical-align mt-20">
          <input
            type="checkbox"
            id="sameAsBilling"
            checked={profile.custom?.sameAsBilling !== false}
            onChange={toggleSameAsBilling}
          />
          <label htmlFor="sameAsBilling" style={{ marginLeft: 10 }}>
            {t("wizard.patientShipping.sameAsBilling")}
          </label>
        </div>
      ),
    },
    // Billing fields — only shown when sameAsBilling === false (must be a strict
    // false; default is true, undefined should also keep them hidden).
    {
      label: t("wizard.patientShipping.billingStreet"),
      type: "text",
      value: profile.custom?.billingAddress?.street || "",
      onChange: setBillingField("street"),
      show: profile.custom?.sameAsBilling === false,
      required: profile.custom?.sameAsBilling === false,
    },
    {
      label: t("wizard.patientShipping.billingAptUnit"),
      type: "text",
      value: profile.custom?.billingAddress?.apt || "",
      onChange: setBillingField("apt"),
      show: profile.custom?.sameAsBilling === false,
    },
    {
      label: t("wizard.patientShipping.billingCity"),
      type: "text",
      value: profile.custom?.billingAddress?.city || "",
      onChange: setBillingField("city"),
      show: profile.custom?.sameAsBilling === false,
      required: profile.custom?.sameAsBilling === false,
    },
    {
      label: t("wizard.patientShipping.billingState"),
      type: "select",
      value: profile.custom?.billingAddress?.state || "",
      onChange: setBillingField("state"),
      show: profile.custom?.sameAsBilling === false,
      required: profile.custom?.sameAsBilling === false,
      options: [
        { key: "", label: t("wizard.patientShipping.selectState") },
        ...states.map((s) => ({ key: s.code, label: s.name })),
      ],
    },
    {
      label: t("wizard.patientShipping.billingZip"),
      type: "text",
      value: profile.custom?.billingAddress?.zip || "",
      onChange: setBillingField("zip"),
      show: profile.custom?.sameAsBilling === false,
      required: profile.custom?.sameAsBilling === false,
    },
  ];
}
